import { BlockTitle } from "@blocks/Blocks";
import Column from "@components/Grid/Column";
import { renderSIAHowToNominateComponents } from "@events-awards/utils/helpers";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import useDropdown from "@hooks/useDropdown";
import useTabs from "@hooks/useTabs";
import { cn } from "@util/helpers";

import { NominationTabs } from "./SIAConstants";

/**
 * Component to render SIA How to Nominate page
 */
const SIAHowToNominatePage = (): React.ReactElement => {
  const { isOpen, toggleDropdown, closeDropdown } = useDropdown();
  const { activeTab, activeLabel, changeTab } = useTabs(NominationTabs);

  const StaticComponent = renderSIAHowToNominateComponents(activeTab);

  return (
    <Column rootClassName="px-0">
      <div
        data-testdata-testid="events-sia-nominate-component"
        className="relative mb-5 w-full font-public-sans lg:hidden"
      >
        <button
          onClick={toggleDropdown}
          className="flex w-full items-center justify-between rounded-full border border-black bg-white px-4 py-2"
        >
          <span
            className={cn(
              "font-semibold text-blue-175 hover:text-black hover:underline",
              { "text-black underline": isOpen }
            )}
          >
            {activeLabel}
          </span>

          <ChevronDownIcon className="ml-2 h-4 w-4 text-black" />
        </button>

        {isOpen ? (
          <div className="absolute z-10 w-full bg-gray-150 py-3">
            {NominationTabs.map(({ key, label }) => (
              <div
                key={key}
                onClick={() => {
                  changeTab(key);
                  closeDropdown();
                }}
                className={cn(
                  "mx-6 my-1 cursor-pointer rounded-lg px-2 py-1 text-black hover:bg-gray-100",
                  { "bg-gray-650 text-white": activeLabel === label }
                )}
              >
                {label}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mb-6 hidden gap-3 lg:flex">
        {NominationTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              changeTab(tab.key);
            }}
            className={cn(
              "text-md rounded-full border border-gray-650 border-gray-750 bg-white px-4 py-2 font-public-sans font-medium text-black text-gray-875 duration-200",
              { "bg-gray-650 text-white": activeTab === tab.key }
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <BlockTitle
        text={NominationTabs.find((t) => t.key === activeTab)?.label || ""}
        rootClassName="text-4xl lg:text-6xl font-medium mb-5"
      />

      {StaticComponent ? <StaticComponent /> : null}
    </Column>
  );
};

export default SIAHowToNominatePage;
